import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../../../Data/UserContext";
import Modal from "../../Modal";
import { ModalCloseIcon } from "../../ModalCloseIcon";
import LoginSignupBanner from "../../Trader/LoginSignupBanner";
import { CricketTeamLogo } from "../../shared/logos";
import { FavouriteTeamStar } from "../../shared/FavouriteTeamStar";
import {
  EVENT_STATUS,
  eventStatusToNumber,
  IN_PLAY_STATUS_IDS,
  isGlobalMatchTradingActive,
} from "../../Trader/Cricket/decimalEventStatus";
import { trackMatchCardGameEntry } from "../../../utils/frontendTrackingEvents";
import { fetchSquaresMatchOverPlays } from "../shared/squaresApi";
import { SquaresPrizePoolBadge } from "../shared/SquaresPrizePoolBadge";
import { getPlayableSquaresOverFromMarkets, getSquares410OverNumbersFromMarkets } from "../shared/squaresOverOdds";
import { commentaryIndicatesTossWon } from "../shared/squaresCommentaryToss";
import {
  isWithinSquaresPlayOpenCountdown,
  scorecardTossConfirmed,
} from "../shared/squaresPlayOpenWindow";
import {
  cricketCurrentOver1BasedFromScoreboard,
  cricketInningsOversDecimalFromScoreboard,
  getCricketMatchCardOvLabel,
  maxOversFromFixtureFormat,
  maxOversFromStreamEvent,
} from "../../Cricket/cricketMatchCardOverDisplay";
import { SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS } from "../shared/squaresConstants";
import { PrematchCountdownLabel } from "../../shared/ui/PrematchCountdownLabel";
import { LobbyActiveGamesSticker } from "../../shared/ui/LobbyActiveGamesSticker";
import { WatchLiveButton } from "../../shared/ui/WatchLiveButton";
import { formatCountdownDuration } from "../../../utils/matchCountdown";
import { mapCricketInnsScoresToTeams, unwrapCricketScorecard } from "../../../utils/cricketInnsTeamScores";

function isStreamLiveEventStatus(status) {
  const n = eventStatusToNumber(status);
  return n != null && IN_PLAY_STATUS_IDS.includes(n);
}
const getCountdownText = (startValue) => {
  if (!startValue) return "";
  const duration = formatCountdownDuration(new Date(startValue).getTime() - Date.now(), "upper");
  return duration || "SOON";
};

/** Delta scorecard `toss` — reject Decimal placeholders like "0 won the toss". */
function scorecardTossKnown(streamData) {
  return scorecardTossConfirmed(streamData?.scorecard?.toss);
}

const SquaresMatchCard = ({
  fixture,
  streamData,
  challengePickMode = false,
  challengeCompactTeamRow = false,
  onChallengePick,
  challengeSending = false,
  challengeOtherSending = false,
  activeSquaresGamesCount = 0,
  hasStream = false,
  onWatchLive,
  /** League mode: open this URL instead of `/squares?match=`. */
  playHref = "",
  /** League mode: force the lime CTA label (skips scheduled/toss gates). */
  ctaLabel = "",
  /** League lobby: Sixes-like type (regular weight, smaller). */
  dense = false,
}) => {
  const { user } = useUser();
  const [liveBat, setLiveBat] = useState("");
  const [scores, setScores] = useState(["0/0", "0/0", "0/0", "0/0"]);
  const [loginModal, setLoginModal] = useState(false);
  const [tradingStatus, setTradingStatus] = useState("");
  const [matchStart, setMatchStart] = useState(() => getCountdownText(fixture.start));
  /** Economy row for the lobby “open” over when match is IN-PLAY (replaces IN-PLAY pill). */
  const [lobbyPoolEcon, setLobbyPoolEcon] = useState(null);
  /** When IN-PLAY + pool loaded: alternate pill between 1st-place prize and IN-PLAY for lobby “juice”. */
  const [lobbyJuiceShowsPrize, setLobbyJuiceShowsPrize] = useState(true);

  const matchParts = useMemo(() => (fixture?.name || "").split(","), [fixture?.name]);
  const competitionName = useMemo(() => fixture?.competition || "", [fixture?.competition]);
  const teams = useMemo(() => (matchParts[0] || "").split(" v ").map((team) => team.trim()).filter(Boolean), [matchParts]);
  const headerIconWrapClass = dense
    ? "flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center"
    : "flex h-[36px] w-[36px] flex-shrink-0 items-center justify-center p-2 md:h-[42px] md:w-[42px] box-border";
  const headerIconInnerClass = dense
    ? "flex h-4 w-4 items-center justify-center"
    : "flex h-[20px] w-[20px] items-center justify-center md:h-[26px] md:w-[26px]";
  const competitionClass = dense
    ? "min-w-0 flex-1 truncate font-chakra text-[10px] font-normal normal-case leading-none text-white"
    : "text-white font-chakra text-[14px] md:text-[16px] font-bold truncate normal-case min-w-0 flex-1";
  const teamRowClass = dense
    ? "flex h-[40px] items-center justify-between bg-white px-2"
    : "flex items-center justify-between bg-white px-3 py-1 md:px-4 md:py-2 h-[48px] md:h-[52px]";
  const teamNameClass = dense
    ? "min-w-0 truncate font-anton text-[14px] font-normal uppercase leading-none text-main"
    : "text-main font-anton text-[18px] md:text-[20px] font-bold truncate uppercase block min-w-0";
  const teamLogoSize = dense ? "32px" : "40px";
  const teamStarSize = dense ? 18 : 22;
  const teamScoreClass = dense
    ? "min-w-[32px] text-right font-chakra text-[12px] font-normal leading-none text-main tabular-nums"
    : `text-main ${SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS} min-w-[40px] tabular-nums`;

  const fixtureStatusUpper = useMemo(() => (fixture?.status || "").toUpperCase(), [fixture?.status]);

  const matchStatus = useMemo(() => {
    const rawFull = fixture?.status || "";
    const es = streamData?.market_update?.event_status ?? streamData?.event?.event_status;
    if (eventStatusToNumber(es) === EVENT_STATUS.BREAK_IN_PLAY) return "BREAK IN PLAY";
    const rawStatus = rawFull.split(" - ")[0] || "";
    let normalized = rawStatus.replace(/\s*\(.*?\)\s*/g, "").trim();
    if (rawFull.includes("Match in Progress")) {
      normalized = "IN-PLAY";
    }
    if (fixtureStatusUpper.includes("BREAK IN PLAY")) {
      normalized = "BREAK IN PLAY";
    }
    return normalized;
  }, [fixture?.status, fixtureStatusUpper, streamData?.market_update?.event_status, streamData?.event?.event_status]);

  const isClosed = useMemo(() => {
    const upper = (matchStatus || "").toUpperCase();
    return (
      upper.startsWith("CANCELLED") ||
      upper.startsWith("MATCH ABANDONED") ||
      upper.startsWith("MATCH COMPLETE") ||
      upper.startsWith("EVENT CLOSED")
    );
  }, [matchStatus]);

  const isInPlay = matchStatus === "IN-PLAY";
  const isTossPendingList =
    fixtureStatusUpper.startsWith("TOSS PENDING") ||
    fixtureStatusUpper.startsWith("TOSS DELAYED") ||
    fixtureStatusUpper.includes("TOSS PENDING") ||
    fixtureStatusUpper.includes("TOSS DELAYED");

  const streamEventStatus = useMemo(() => {
    const mu = streamData?.market_update;
    if (!mu) return null;
    if (mu.event_status != null && mu.event_status !== "") return mu.event_status;
    return mu.market_update?.event_status ?? null;
  }, [streamData?.market_update]);

  const isStreamInPlayPhase = useMemo(() => isStreamLiveEventStatus(streamEventStatus), [streamEventStatus]);

  const tossKnownFromCommentary = useMemo(
    () => commentaryIndicatesTossWon(streamData?.commentary),
    [streamData]
  );
  const scorecardToss = useMemo(() => scorecardTossKnown(streamData), [streamData]);

  /**
   * TOSS PENDING (fixture list): only commentary with “won/wins the toss” opens Squares (not scorecard.toss alone).
   * Other states: commentary or scorecard toss, plus in-play paths below.
   */
  const tossAllowsSquaresPlay = useMemo(() => {
    if (isTossPendingList) return tossKnownFromCommentary;
    return tossKnownFromCommentary || scorecardToss;
  }, [isTossPendingList, tossKnownFromCommentary, scorecardToss]);

  /** Playable once toss is confirmed, within T-30 of start, or match is in-play. */
  const livePhaseForSquares = useMemo(() => {
    if (isClosed) return false;
    return (
      isInPlay ||
      matchStatus === "BREAK IN PLAY" ||
      isStreamInPlayPhase ||
      tossAllowsSquaresPlay ||
      isWithinSquaresPlayOpenCountdown(fixture?.start)
    );
  }, [isClosed, isInPlay, matchStatus, isStreamInPlayPhase, tossAllowsSquaresPlay, fixture?.start]);

  const tradingOpen = isGlobalMatchTradingActive(tradingStatus);
  const hasTradingSignal = tradingStatus != null && tradingStatus !== "";
  /** Block when the match feed marks trading closed and we are not in a playable phase. */
  const blockPlaySquares = hasTradingSignal && !tradingOpen && !livePhaseForSquares;

  const isMatchScheduledOnly =
    !livePhaseForSquares &&
    !fixtureStatusUpper.startsWith("CANCELLED") &&
    !fixtureStatusUpper.includes("MATCH ABANDONED") &&
    (fixtureStatusUpper.includes("MATCH SCHEDULED") || matchStatus.toUpperCase() === "MATCH SCHEDULED");

  /** Max legal overs for the innings (stream “Max Overs” or T20/ODI from fixture list). Used to cap lobby CTA over. */
  const lobbyInningsMaxOvers = useMemo(
    () => maxOversFromStreamEvent(streamData) ?? maxOversFromFixtureFormat(fixture?.format),
    [streamData, fixture?.format],
  );

  /**
   * Cricket current over (1-based): floor(full + part/6) + 1 from scoreboard.
   * When the feed reports a completed innings as full=20 part=0, that formula returns 21 (past the last over of a T20).
   * Clamp to max overs so the lobby never shows “PLAY OVER 21” after 20/20 in the first innings.
   */
  const cricketCurrentOver = useMemo(() => {
    if (!livePhaseForSquares || !streamData?.event) return null;
    const eventObj = streamData.event?.event ?? streamData.event;
    const raw = cricketCurrentOver1BasedFromScoreboard(eventObj?.scoreboard);
    if (raw == null) return null;
    const cap = lobbyInningsMaxOvers;
    if (cap != null && Number.isFinite(cap) && cap > 0) {
      return Math.min(raw, cap);
    }
    return raw;
  }, [livePhaseForSquares, streamData?.event, lobbyInningsMaxOvers]);

  /** Over with an open 410 market (what Squares actually accepts). Never fall back to scoreboard-only over. */
  const playOverNumber = useMemo(() => {
    if (!livePhaseForSquares) return null;
    const mu = streamData?.market_update;
    const markets = mu?.markets ?? mu?.market_update?.markets;
    const overs410 = getSquares410OverNumbersFromMarkets(markets);
    const fromMarkets = getPlayableSquaresOverFromMarkets(markets, cricketCurrentOver);
    let n = fromMarkets ?? null;
    const cap = lobbyInningsMaxOvers;
    if (n != null && cap != null && Number.isFinite(cap) && cap > 0) {
      n = Math.min(Number(n), cap);
    }
    const eventObj = streamData?.event?.event ?? streamData?.event;
    const inningsOversDec = cricketInningsOversDecimalFromScoreboard(eventObj?.scoreboard);
    const max = lobbyInningsMaxOvers;
    if (
      inningsOversDec != null &&
      max != null &&
      Number.isFinite(max) &&
      max > 0 &&
      inningsOversDec >= max
    ) {
      if (overs410.has(1)) return 1;
      return null;
    }
    return n;
  }, [livePhaseForSquares, streamData?.market_update, streamData?.event, cricketCurrentOver, streamEventStatus, lobbyInningsMaxOvers]);

  const isBreakInPlay = eventStatusToNumber(streamEventStatus) === EVENT_STATUS.BREAK_IN_PLAY;
  const statusText = useMemo(() => {
    if (isClosed) return "FINISHED";
    if (isTossPendingList && !tossKnownFromCommentary) return "TOSS PENDING";
    if (isTossPendingList && tossKnownFromCommentary) return "PRE-MATCH";
    if (isMatchScheduledOnly) return "SCHEDULED";
    if (livePhaseForSquares && isBreakInPlay) return "BREAK IN PLAY";
    if (isInPlay || (livePhaseForSquares && !isMatchScheduledOnly)) return "IN-PLAY";
    if (blockPlaySquares) return matchStart === "SOON" ? "STARTING SOON" : `STARTS IN ${matchStart}`;
    if (matchStatus.toUpperCase() === "MATCH SCHEDULED") return matchStart === "SOON" ? "STARTING SOON" : matchStart ? `STARTS IN ${matchStart}` : "MATCH SCHEDULED";
    return matchStatus || "";
  }, [
    isClosed,
    isTossPendingList,
    tossKnownFromCommentary,
    isMatchScheduledOnly,
    livePhaseForSquares,
    isBreakInPlay,
    isInPlay,
    blockPlaySquares,
    matchStart,
    matchStatus,
  ]);

  const statusColor =
    isTossPendingList && !isClosed
      ? "text-lime"
      : livePhaseForSquares && isBreakInPlay && !isClosed
        ? "text-[#a855f7]"
        : isInPlay || isStreamInPlayPhase
          ? "text-pink"
          : "text-white";

  const inningsForLobbyPool = useMemo(() => {
    const raw = streamData?.scorecard?.inns_now;
    const n = parseInt(String(raw ?? "1"), 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }, [streamData?.scorecard?.inns_now]);

  useEffect(() => {
    if (!fixture?.match_id || statusText !== "IN-PLAY") {
      setLobbyPoolEcon(null);
      return;
    }
    const overNum = playOverNumber;
    if (overNum == null || !Number.isFinite(Number(overNum)) || Number(overNum) < 1) {
      setLobbyPoolEcon(null);
      return;
    }
    let cancelled = false;
    const tid = setTimeout(() => {
      const curHint = cricketCurrentOver != null && Number.isFinite(Number(cricketCurrentOver)) ? Math.floor(Number(cricketCurrentOver)) : Math.floor(Number(overNum));
      const params = new URLSearchParams({
        matchId: String(fixture.match_id),
        inningsNumber: String(inningsForLobbyPool),
        currentOverNumber: String(Math.max(1, curHint)),
        bootstrap: "1",
      });
      const fmt = fixture?.format;
      if (fmt != null && String(fmt).trim() !== "") {
        const fn = parseInt(String(fmt), 10);
        if (Number.isFinite(fn)) params.set("format", String(fn));
      }
      fetchSquaresMatchOverPlays(params)
        .then((data) => {
          if (cancelled) return;
          const on = Math.floor(Number(overNum));
          const econ = data?.over_economy?.[String(on)] ?? data?.over_economy?.[on];
          const pc = econ?.pool_entrant_count;
          if (pc != null && Number.isFinite(Number(pc))) {
            setLobbyPoolEcon({ pool_entrant_count: Number(pc) });
          } else {
            setLobbyPoolEcon(null);
          }
        })
        .catch(() => {
          if (!cancelled) setLobbyPoolEcon(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(tid);
    };
  }, [
    fixture?.match_id,
    fixture?.format,
    statusText,
    playOverNumber,
    cricketCurrentOver,
    inningsForLobbyPool,
  ]);

  useEffect(() => {
    if (lobbyPoolEcon != null) setLobbyJuiceShowsPrize(true);
  }, [lobbyPoolEcon?.pool_entrant_count]);

  useEffect(() => {
    if (statusText !== "IN-PLAY" || lobbyPoolEcon == null) return undefined;
    const id = setInterval(() => setLobbyJuiceShowsPrize((v) => !v), 2400);
    return () => clearInterval(id);
  }, [statusText, lobbyPoolEcon]);

  const ovDisplayLabel = useMemo(
    () => (livePhaseForSquares ? getCricketMatchCardOvLabel(streamData, fixture?.format) : null),
    [livePhaseForSquares, streamData, fixture?.format]
  );

  const scoreRef = useRef(scores);
  useEffect(() => {
    scoreRef.current = scores;
  }, [scores]);

  useEffect(() => {
    setMatchStart(getCountdownText(fixture.start));
  }, [fixture.start]);

  /** Keep OPENS IN countdown moving for scheduled lobby cards (stream may be light). */
  useEffect(() => {
    if (!isMatchScheduledOnly || isClosed || !fixture?.start) return;
    const src = fixture.start;
    const tick = () => setMatchStart(getCountdownText(src));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [isMatchScheduledOnly, isClosed, fixture?.start]);

  useEffect(() => {
    if (!streamData) return;

    if (streamData.match_update) {
      if (streamData.match_update.betting_status != null && streamData.match_update.betting_status !== "") {
        setTradingStatus(streamData.match_update.betting_status);
      }
      if (streamData.match_update.start_datetime) {
        setMatchStart(getCountdownText(streamData.match_update.start_datetime));
      }
    }

    const muBs = streamData.market_update?.betting_status ?? streamData.market_update?.market_update?.betting_status;
    if (muBs != null && muBs !== "") {
      setTradingStatus(muBs);
    }

    if (!streamData.scorecard || teams.length < 2) return;

    const sc = unwrapCricketScorecard(streamData.scorecard);
    if (!sc) return;

    const { scores: nextScore, batNow: nextLiveBat } = mapCricketInnsScoresToTeams(sc, teams);
    setLiveBat(nextLiveBat);

    const prev = scoreRef.current;
    const nextEmpty = [nextScore[0], nextScore[1]].every((s) => s === "0/0" || s === "-" || !s);
    const prevHasData = [prev[0], prev[1]].some((s) => s && s !== "0/0" && s !== "-");
    if (nextEmpty && prevHasData) return;

    if (prev[0] !== nextScore[0] || prev[1] !== nextScore[1] || prev[2] !== nextScore[2] || prev[3] !== nextScore[3]) {
      setScores(nextScore);
    }
  }, [streamData, teams]);

  const handlePlaySquares = useCallback(() => {
    if (playHref) {
      if (user) {
        trackMatchCardGameEntry({
          user_id: user.userId,
          game_type: "squares",
          fixture_id: fixture.match_id,
          sport: "cricket",
          competition_name: competitionName || undefined,
          cta_location: "squares_lobby",
          target_url: playHref,
        });
        window.location.href = playHref;
        return;
      }
      setLoginModal(true);
      return;
    }
    if (isClosed || blockPlaySquares || !livePhaseForSquares) return;
    if (challengePickMode && onChallengePick) {
      if (challengeSending || challengeOtherSending) return;
      onChallengePick();
      return;
    }
    if (user) {
      trackMatchCardGameEntry({
        user_id: user.userId,
        game_type: "squares",
        fixture_id: fixture.match_id,
        sport: "cricket",
        competition_name: competitionName || undefined,
        cta_location: "squares_lobby",
        target_url: `/squares?match=${fixture.match_id}`,
      });
      window.location.href = `/squares?match=${fixture.match_id}`;
      return;
    }
    setLoginModal(true);
  }, [
    challengeOtherSending,
    challengePickMode,
    challengeSending,
    competitionName,
    fixture.match_id,
    isClosed,
    blockPlaySquares,
    livePhaseForSquares,
    onChallengePick,
    playHref,
    user,
  ]);

  const gamesStickerEl = (
    <LobbyActiveGamesSticker
      count={activeSquaresGamesCount}
      avatarSrc={user?.avatar || "/img/avatar.png"}
      xpWeekCrown={user?.xp_week_crown}
      labelSingular="Play"
      labelPlural="Plays"
    />
  );

  const renderAction = () => {
    if (playHref) {
      return (
        <div className="relative flex items-center h-full" id="aa">
          <div
            className="bg-lime hover:bg-limehover text-main font-anton text-[12px] leading-[18px] px-3 md:px-4 py-0 uppercase whitespace-nowrap min-w-[80px] md:min-w-[120px] flex items-center justify-center self-stretch cursor-pointer"
            style={{ letterSpacing: "0%", textAlign: "center" }}
            onClick={handlePlaySquares}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePlaySquares();
              }
            }}
          >
            {ctaLabel || "PLAY"}
          </div>
        </div>
      );
    }
    if (isClosed) {
      return <div className="relative flex items-center h-full" id="aa">
            <span
                className="bg-lime hover:bg-limehover text-main font-anton text-[12px] leading-[18px] px-3 md:px-4 py-0 uppercase whitespace-nowrap min-w-[80px] md:min-w-[120px] flex items-center justify-center self-stretch"
                style={{ letterSpacing: "0%", textAlign: "center" }}
            >
                MATCH COMPLETED
            </span>
        </div>
    }
    if (isMatchScheduledOnly) {
      const opensDisplay =
        matchStart === "SOON" ? "OPENS SOON" : `OPENS IN ${matchStart || "—"}`;
      return (
        <div className="relative flex items-center h-full pl-0 pr-1 md:pr-1.5" id="aa">
          <span
            className={`${SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS} text-lime whitespace-nowrap tabular-nums`}
          >
            <PrematchCountdownLabel text={opensDisplay} />
          </span>
        </div>
      );
    }
    if (isTossPendingList && !tossKnownFromCommentary && !isClosed) {
      return (
        <div className="relative flex items-center h-full pl-0 pr-1 md:pr-1.5" id="aa">
          <span
            className={`${SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS} text-white/90 whitespace-nowrap`}
          >
            AWAITING TOSS
          </span>
        </div>
      );
    }
    if (blockPlaySquares) {
      return (
          <div className="relative flex items-center h-full" id="aa">
              <div
                  className="bg-lime hover:bg-limehover text-main font-anton text-[12px] leading-[18px] px-3 md:px-4 py-0 uppercase whitespace-nowrap min-w-[80px] md:min-w-[120px] flex items-center justify-center self-stretch"
                  style={{ letterSpacing: "0%", textAlign: "center" }}
                  onClick={handlePlaySquares}
              >
                  <PrematchCountdownLabel
                    text={matchStart === "SOON" ? "STARTS SOON" : `STARTS IN ${matchStart}`}
                  />
              </div>
          </div>
      );
    }
    const challengeCtaDisabled =
      challengePickMode && (challengeSending || challengeOtherSending);
    const challengeCtaLabel = challengeSending
      ? "SENDING…"
      : playOverNumber != null
        ? `CHALLENGE · OVER ${playOverNumber}`
        : "CHALLENGE";
    const playCtaLabel =
      playOverNumber != null ? `PLAY OVER ${playOverNumber}` : "GAME NOT OPEN YET";

    return (
        <div className="relative flex items-center h-full" id="aa">
            <div
                className={`bg-lime text-main font-anton text-[12px] leading-[18px] px-3 md:px-4 py-0 uppercase whitespace-nowrap min-w-[80px] md:min-w-[120px] flex items-center justify-center self-stretch ${
                  challengeCtaDisabled ? "cursor-not-allowed opacity-60" : "hover:bg-limehover cursor-pointer"
                }`}
                style={{ letterSpacing: "0%", textAlign: "center" }}
                onClick={handlePlaySquares}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handlePlaySquares();
                  }
                }}
            >
                {challengePickMode ? challengeCtaLabel : playCtaLabel}
            </div>
        </div>
    );
  };

  return (
    <>
      <div className="bg-mainH border border-main60 w-full overflow-visible">
        <div className="relative z-10 flex items-stretch justify-between overflow-visible pb-1">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div
              className={headerIconWrapClass}
              style={{ backgroundColor: "#3F1A6D" }}
            >
              <span
                className={headerIconInnerClass}
                aria-hidden
              >
                <img
                  src="/assets/cricket.webp"
                  alt=""
                  className="block h-full w-full min-h-0 min-w-0 object-contain object-center"
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                />
              </span>
            </div>
            <span className={competitionClass}>{competitionName || "SQUARES"}</span>
          </div>
          <div
            className={`relative z-10 flex items-center flex-shrink-0 overflow-visible ${
              isMatchScheduledOnly ? "gap-0.5" : statusText === "IN-PLAY" && lobbyPoolEcon != null ? "gap-1" : "gap-1.5"
            }`}
          >
            {statusText === "IN-PLAY" && lobbyPoolEcon != null ? (
              <span
                className={`${SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS} inline-grid max-w-[min(220px,70vw)] flex-shrink-0 grid-cols-1 grid-rows-1 rounded-full px-2 py-1 ${statusColor}`}
                aria-label="Match live; first place prize rotates in this pill"
              >
                <span
                  className={`col-start-1 row-start-1 flex w-full min-w-0 items-center justify-end transition-opacity duration-500 ease-in-out ${
                    lobbyJuiceShowsPrize ? "z-[1] opacity-100" : "pointer-events-none opacity-0"
                  }`}
                  aria-hidden={!lobbyJuiceShowsPrize}
                >
                  <SquaresPrizePoolBadge econ={lobbyPoolEcon} className="text-[9px] sm:text-[10px] md:text-[11px]" />
                </span>
                <span
                  className={`col-start-1 row-start-1 flex w-full items-center justify-end transition-opacity duration-500 ease-in-out ${
                    lobbyJuiceShowsPrize ? "pointer-events-none opacity-0" : "z-[1] opacity-100"
                  }`}
                  aria-hidden={lobbyJuiceShowsPrize}
                >
                  <span className="text-nowrap">MATCH LIVE</span>
                </span>
              </span>
            ) : (
              <PrematchCountdownLabel
                text={statusText}
                className={`${SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS} whitespace-nowrap inline-flex items-center px-2 py-0.5 rounded-full ${statusColor}`}
              />
            )}
            {hasStream && livePhaseForSquares && onWatchLive ? (
              <WatchLiveButton
                fixtureId={String(fixture?.match_id ?? "")}
                ctaLocation="squares_lobby"
                onClick={onWatchLive}
              />
            ) : null}
            {renderAction()}
            {gamesStickerEl}
          </div>
        </div>

        {challengePickMode && challengeCompactTeamRow && teams.length >= 2 ? (
          <>
            <div className="w-full h-[1px] bg-main" />
            <div className="flex w-full min-h-[44px] items-center gap-1.5 bg-white px-2 py-2 md:min-h-[48px] md:gap-2 md:px-3">
              <div className="flex-shrink-0">
                <CricketTeamLogo teamName={teams[0]} width="36px" height="36px" />
              </div>
              <div className="flex min-w-0 flex-1 items-center justify-end gap-1 overflow-hidden">
                <span className="min-w-0 truncate text-right font-anton text-[14px] font-bold uppercase text-main md:text-[16px]">
                  {teams[0]}
                </span>
                <FavouriteTeamStar teamName={teams[0]} size={20} />
              </div>
              <span
                className="flex-shrink-0 font-anton text-[11px] font-bold uppercase text-main/70 md:text-[12px]"
                aria-hidden
              >
                V
              </span>
              <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                <FavouriteTeamStar teamName={teams[1]} size={20} />
                <span className="min-w-0 truncate text-left font-anton text-[14px] font-bold uppercase text-main md:text-[16px]">
                  {teams[1]}
                </span>
              </div>
              <div className="flex-shrink-0">
                <CricketTeamLogo teamName={teams[1]} width="36px" height="36px" />
              </div>
            </div>
          </>
        ) : teams.length > 0 ? (
          teams.slice(0, 2).map((team, idx) => {
            const displayScore = idx === 0 ? scores[0] : scores[1];
            const isBatting = livePhaseForSquares && liveBat === team;
            return (
              <React.Fragment key={`${fixture.match_id}-${team}`}>
                <div className="w-full h-[1px] bg-main" />
                <div className={teamRowClass}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 mt-1 md:mt-0">
                      <CricketTeamLogo teamName={team} width={teamLogoSize} height={teamLogoSize} />
                    </div>
                    <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
                      <span className={teamNameClass}>{team}</span>
                      <FavouriteTeamStar teamName={team} size={teamStarSize} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {isBatting && ovDisplayLabel != null ? (
                      <span
                        className={`text-main/65 ${SQUARES_LOBBY_MATCH_CARD_RIGHT_TEXT_CLASS} tabular-nums`}
                        title="Current over / max overs (innings)"
                      >
                        {ovDisplayLabel}
                      </span>
                    ) : null}
                    {isBatting && (
                      <div className="relative flex-shrink-0 w-[12px] h-[12px] flex items-center justify-center">
                        <div className="absolute w-[10px] h-[10px] rounded-full pulse-pink-glow" aria-hidden="true" />
                        <div className="relative z-10 w-[8px] h-[8px] bg-pink rounded-full" />
                      </div>
                    )}
                    <span className={teamScoreClass}>
                      {displayScore || "0/0"}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        ) : (
          <div className="w-full h-[1px] bg-main">
            <div className="text-white font-chakra text-[12px] p-2">{fixture.name}</div>
          </div>
        )}
      </div>

      <Modal
        isOpen={loginModal}
        onRequestClose={() => setLoginModal(false)}
        style={{
          overlay: {
            backgroundColor: "rgba(27, 6, 47, 0.7)",
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            maxWidth: "400px",
            background: "transparent",
            border: "none",
            padding: "0",
          },
        }}
        contentLabel="Login or Sign Up"
        ariaHideApp={false}
      >
        <div className="relative">
          <button
            className="absolute top-2 right-2 cursor-pointer bg-white hover:bg-gray-100 rounded-none p-1.5 flex items-center justify-center shadow-lg transition-colors z-10"
            onClick={() => setLoginModal(false)}
            aria-label="Close"
          >
            <ModalCloseIcon width={28} height={28} fill="#1b062f" />
          </button>
          <LoginSignupBanner deeplink={playHref || `/squares?match=${fixture.match_id}`} />
        </div>
      </Modal>
    </>
  );
};

const areEqual = (prevProps, nextProps) => {
  const p = prevProps.fixture || {};
  const n = nextProps.fixture || {};
  if (
    p.match_id !== n.match_id ||
    p.name !== n.name ||
    p.status !== n.status ||
    p.start !== n.start ||
    p.competition !== n.competition ||
    p.format !== n.format
  ) {
    return false;
  }
  if (prevProps.challengePickMode !== nextProps.challengePickMode) return false;
  if (prevProps.challengeCompactTeamRow !== nextProps.challengeCompactTeamRow) return false;
  if (prevProps.challengeSending !== nextProps.challengeSending) return false;
  if (prevProps.challengeOtherSending !== nextProps.challengeOtherSending) return false;
  if (prevProps.activeSquaresGamesCount !== nextProps.activeSquaresGamesCount) return false;
  if (prevProps.dense !== nextProps.dense) return false;
  return prevProps.streamData === nextProps.streamData;
};

export default React.memo(SquaresMatchCard, areEqual);
